import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { IDataboxProgressionIcon } from 'src/app/api/modules/core/dynamic/databoxes/progression-icon/IDataboxProgressionIcon';
import { GenerateFaIcon } from 'src/app/factories/generators/icon-fa.generator';
import { GenerateIconState } from 'src/app/factories/generators/icon-state.generator';
import { IconStateComponent } from '../../../../../icons/components/icon-state/icon-state.component';

/**
 * The icon state set used when the configuration does not provide a usable
 * one: an empty circle for `default` and a filled check for `checked`.
 */
function defaultState() {
  return GenerateIconState({
    states: [GenerateFaIcon({ name: 'far:circle', state: 'default' }), GenerateFaIcon({ name: 'fas:circle-check', state: 'checked' })],
  });
}

/**
 * Displays progression as a row of icons inside a DataboxContainer (e.g.
 * "x x o o o" for 2/5): one icon per unit of `total`, checked up to the
 * current progress pushed in through {@link update}.
 *
 * An unconfigured databox renders nothing until a total arrives (from the
 * config or an update).
 *
 * See {@link IDataboxProgressionIcon} for the configuration structure.
 */
@Component({
  selector: 'app-databox-progression-icon',
  templateUrl: './databox-progression-icon.component.html',
  styleUrl: './databox-progression-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconStateComponent],
})
export class DataboxProgressionIconComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources; the built-in defaults
   * are used until a usable config arrives.
   */
  readonly config = input<IDataboxProgressionIcon | null>();

  /**
   * The last valid progress received via {@link update}, or `undefined`
   * while no progress has been made.
   */
  private readonly current = signal<number | undefined>(undefined);

  /**
   * A total received via {@link update}, which takes precedence over the
   * configured total until the next config change.
   */
  private readonly totalOverride = signal<number | undefined>(undefined);

  /**
   * The validated icon state set, falling back to the defaults (and
   * collecting warnings, logged by an effect) when the config is unusable.
   */
  private readonly viewState = computed(() => {
    const configuration = this.config();
    const warnings: string[] = [];

    let activeState = defaultState();
    const configuredStates = configuration?.state?.states;
    if (configuredStates) {
      const hasDefault = configuredStates.some((icon) => icon.state === 'default');
      const hasChecked = configuredStates.some((icon) => icon.state === 'checked');

      if (!hasDefault) {
        warnings.push(`No provided icon state has a state name of "default". A default will be used.`);
      }
      if (!hasChecked) {
        warnings.push(`No provided icon state has a state name of "checked". A default will be used.`);
      }

      if (hasDefault && hasChecked) {
        activeState = configuration.state;
      }
    }

    return { activeState, warnings };
  });

  /**
   * One entry per icon to display: the shared icon state set plus whether
   * this position counts as progress.
   */
  protected readonly states = computed(() => {
    const { activeState } = this.viewState();
    const configuredTotal = this.config()?.total;
    const total = this.totalOverride() ?? (typeof configuredTotal === 'number' ? configuredTotal : 0);
    const current = this.current() ?? 0;

    return Array.from({ length: Math.max(0, total) }, (_, index) => ({
      state: activeState,
      checked: index < current,
    }));
  });

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      for (const warning of this.viewState().warnings) {
        this.loggerService.warn(`[DataboxProgressionIcon] - ${warning}`);
      }
    });

    // a new config returns the databox to its configured, unchecked state
    effect(() => {
      this.config();
      untracked(() => {
        this.current.set(undefined);
        this.totalOverride.set(undefined);
      });
    });
  }

  /**
   * Updates the component with the user's progress.
   *
   * @param current - the current progress of the user
   * @param total - the total amount of progress possible, optional
   */
  update(current: number, total?: number) {
    if (typeof total === 'number') {
      this.totalOverride.set(total);
    }
    if (typeof current === 'number') {
      this.current.set(current);
    }
  }
}
